import i18n from 'i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import ChainedBackend from 'i18next-chained-backend';
import HttpBackend from 'i18next-http-backend';
import resourcesToBackend from 'i18next-resources-to-backend';
import { initReactI18next } from 'react-i18next';

import { keys } from 'app/config/keys';
import { NodeEnvironment } from 'app/types';

import resources from './resources';

// This was a good resource on organizing translations. I found other resources that said similar things, but
// this summarized them well.
// https://gist.github.com/Lemmings19/38225ba78c8cce4f68318a1e2410d325
// Namespaces:
//      * use top level folder structure: payees, payors, etc.
//      * "components" and maybe other things that don't fit under the top level folder structure.
//      * "common" for shared strings.
// Keys:
//      * use camelCase for keys
//      * {namespace}:{page/area/component/high level owner}:{specific descriptive key}
//      * keep the keys flat.

const i18nEnvConfig: Record<NodeEnvironment, boolean> = {
  development: true,
  local: true,
  onboarding: false,
  production: false,
  staging: true,
  test: true,
};

const enablei18n = i18nEnvConfig[keys.NODE_ENV];
const debug = keys.__DEV__;

// http wont work in tests because a server isnt working, so bypass it entirely because it causes failures
const useHttpBackend = keys.NODE_ENV !== 'test';

export const defaultNS = 'common';
// this key is the same as the default, but that value is not exported from the library
// included here to make it obvious what the key is, and prevent unexpected changes
// https://github.com/i18next/i18next-browser-languageDetector?tab=readme-ov-file#detector-options
const i18nLocalStorageKey = 'i18nextLng';

export const cleari18nLocalStorage = () => {
  localStorage.removeItem(i18nLocalStorageKey);
};

export const initPromise = i18n
// load translation using http -> see /public/locales (i.e.
// https://github.com/i18next/react-i18next/tree/master/example/react/public/locales)
// learn more: https://github.com/i18next/i18next-http-backend
// want your translations to be loaded from a professional CDN? =>
// https://github.com/locize/react-tutorial#step-2---use-the-locize-cdn
  .use(ChainedBackend)
// detect user language
// learn more: https://github.com/i18next/i18next-browser-languageDetector
  .use(LanguageDetector)
// pass the i18n instance to react-i18next.
  .use(initReactI18next)
// init i18next
// for all options read: https://www.i18next.com/overview/configuration-options
  .init({
    backend: {
      backendOptions: [{
        // for now just use english for every locale in prod until we have everything translated.
        // Once we have real translations, this can be removed
        loadPath: enablei18n
          ? '/assets/locales/{{lng}}/{{ns}}.json'
          : '/assets/locales/en/{{ns}}.json',
      }],
      backends: [
        ...useHttpBackend ? [HttpBackend] : [],
        ...enablei18n
          ? [resourcesToBackend((language: string, namespace: string) => {
            return import(`app/assets/locales/${language}/${namespace}.json`);
          })]
          : [],
      ],
    },
    debug,
    defaultNS,
    detection: {
      lookupLocalStorage: i18nLocalStorageKey,
      lookupSessionStorage: i18nLocalStorageKey,
    },
    fallbackLng: 'en',
    interpolation: {
      escapeValue: false, // not needed for react as it escapes by default
    },
    load: 'languageOnly', // if we need to support regional dialects, change this to 'all' or remove it
    ns: Object.keys(resources),
  });

export default i18n;
